<script setup lang="ts">
import { computed, ref } from "vue";

// Презентационная строка аудиоплеера: acid-фон, чёрная квадратная кнопка
// плей/пауза слева, чёрная дорожка с перемоткой, время. Состоянием управляет
// родитель (пузырь в ленте, глобальная полоса, превью записи).

const props = defineProps<{
    playing: boolean;
    // 0..1
    progress: number;
    timeLabel: string;
    // Волна (пики 0..1) — дорожка из столбиков; без неё — сплошная линия.
    peaks?: number[];
    // Заголовок трека (глобальная полоса); в пузыре не показывается.
    title?: string;
    closable?: boolean;
}>();

// Плотность отрисовки не зависит от числа пиков в данных (у старых сообщений
// их меньше): вход ресемплится в фикс. число баров — толщина всегда одинаковая.
const RENDER_BARS = 74;

const renderPeaks = computed(() => {
    const src = props.peaks;
    if (!src?.length) {
        return null;
    }
    const out: number[] = [];
    for (let i = 0; i < RENDER_BARS; i++) {
        out.push(
            src[
                Math.min(
                    src.length - 1,
                    Math.floor((i * src.length) / RENDER_BARS)
                )
            ]
        );
    }
    return out;
});

const emit = defineEmits<{ toggle: []; seek: [ratio: number]; close: [] }>();

const trackRef = ref<HTMLElement | null>(null);

function onTrackClick(e: MouseEvent): void {
    const el = trackRef.value;
    if (!el) {
        return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) {
        return;
    }
    emit(
        "seek",
        Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    );
}
</script>

<template>
    <div class="mc-aplayer">
        <button
            class="mc-aplayer__btn"
            :title="playing ? 'Пауза' : 'Играть'"
            @click.stop="emit('toggle')"
        >
            <svg v-if="playing" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
            </svg>
        </button>
        <span v-if="title" class="mc-aplayer__title">{{ title }}</span>
        <div class="mc-aplayer__main">
            <div
                ref="trackRef"
                class="mc-aplayer__track"
                @click.stop="onTrackClick"
            >
                <!-- Волна: два одинаковых слоя столбиков — базовый приглушённый и
                 сплошной чёрный, клипнутый по прогрессу. -->
                <template v-if="renderPeaks">
                    <div class="mc-aplayer__bars">
                        <span
                            v-for="(p, i) in renderPeaks"
                            :key="i"
                            :style="{ height: `${Math.max(10, p * 100)}%` }"
                        />
                    </div>
                    <div
                        class="mc-aplayer__bars mc-aplayer__bars_fill"
                        :style="{
                            clipPath: `inset(0 ${
                                (1 - Math.min(1, Math.max(0, progress))) * 100
                            }% 0 0)`,
                        }"
                    >
                        <span
                            v-for="(p, i) in renderPeaks"
                            :key="i"
                            :style="{ height: `${Math.max(10, p * 100)}%` }"
                        />
                    </div>
                </template>
                <template v-else>
                    <div class="mc-aplayer__line" />
                    <div
                        class="mc-aplayer__fill"
                        :style="{
                            width: `${Math.min(1, Math.max(0, progress)) * 100}%`,
                        }"
                    />
                </template>
            </div>
            <!-- Под дорожкой: длительность слева, мета (время/прочитанность)
                 справа — отдаёт родитель через слот. -->
            <div class="mc-aplayer__row">
                <span class="mc-aplayer__time">{{ timeLabel }}</span>
                <span class="mc-aplayer__meta"><slot name="meta" /></span>
            </div>
        </div>
        <button
            v-if="closable"
            class="mc-aplayer__close"
            title="Закрыть"
            @click.stop="emit('close')"
        >
            ✕
        </button>
    </div>
</template>

<style scoped lang="scss">
.mc-aplayer {
    display: flex;
    align-items: stretch;
    gap: 10px;
    min-width: 240px;

    &__btn {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        // На всю высоту плеера (дорожка + строка под ней).
        align-self: stretch;
        min-height: 40px;
        padding: 0;
        color: var(--mc-acid);
        background: #0a0c0a;
        border: none;
        cursor: pointer;

        svg {
            width: 18px;
            height: 18px;
        }
    }

    &__title {
        min-width: 0;
        max-width: 40%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        color: var(--mc-fg-message);
        font-family: var(--mc-mono);
        font-size: 12px;
        font-weight: 700;
    }

    // Колонка справа от кнопки: дорожка сверху, строка «длительность · мета» под ней.
    &__main {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    // Кликабельная зона выше самой дорожки — проще попадать.
    &__track {
        position: relative;
        height: 16px;
        margin-top: 2px;
        cursor: pointer;
    }

    &__row {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 8px;
        flex: 1;
    }

    &__meta {
        display: inline-flex;
        color: var(--mc-fg-message);
    }

    // Слои волны: столбики на всю ширину, центрированы по вертикали.
    &__bars {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        gap: 1px;

        span {
            flex: 1;
            min-width: 1px;
            background: color-mix(
                in srgb,
                var(--mc-fg-message) 30%,
                transparent
            );
        }

        // Проигранная часть: сплошной цвет, окно клипа тянется прогрессом.
        &_fill {
            // Чуть длиннее интервала timeupdate (~250мс) — сглаживает шаги.
            transition: clip-path 0.25s linear;

            span {
                background: var(--mc-fg-message);
            }
        }
    }

    // Фолбэк без волны: сплошная линия.
    &__line {
        position: absolute;
        inset: 50% 0 auto;
        height: 3px;
        transform: translateY(-50%);
        background: color-mix(in srgb, var(--mc-fg-message) 25%, transparent);
    }

    &__fill {
        position: absolute;
        left: 0;
        top: 50%;
        height: 3px;
        transform: translateY(-50%);
        background: var(--mc-fg-message);
        // Чуть длиннее интервала timeupdate (~250мс) — сглаживает шаги.
        transition: width 0.25s linear;
    }

    &__time {
        flex-shrink: 0;
        color: var(--mc-fg-message);
        font-size: 10px;
        letter-spacing: 0.04em;
    }

    &__close {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        padding: 0;
        color: rgba(0, 0, 0, 0.7);
        background: none;
        border: none;
        cursor: pointer;
        font-size: 12px;

        &:hover {
            color: #000;
        }
    }
}
</style>
